import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import {
  getPendingQualityApprovals,
  getQualityApprovals,
  approveQualityApproval,
  rejectQualityApproval,
} from "../../api/quality";
import { getBackendOrigin } from "../../api/client";
import { EmptyState, FormDialog, PageHeader } from "../../components/ui";

function fmtDateTime(val) {
  if (!val) return "-";
  return String(val).replace("T", " ").slice(0, 19);
}

function buildImageUrl(filePath) {
  if (!filePath) return null;
  if (String(filePath).startsWith("http://") || String(filePath).startsWith("https://")) return filePath;
  return `${getBackendOrigin()}${filePath}`;
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const commaIndex = result.indexOf(",");
      resolve({
        file_name: file.name,
        mime_type: file.type || "image/jpeg",
        data_base64: commaIndex >= 0 ? result.slice(commaIndex + 1) : result,
        preview_url: result,
      });
    };
    reader.onerror = () => reject(new Error("Dosya okunamadi"));
    reader.readAsDataURL(file);
  });
}

function getQcStatusLabel(row) {
  const status = String(row?.qc_status || "").toLowerCase();
  if (status === "approved") return "Onaylandi";
  if (status === "rejected") return "Reddedildi";
  if (status === "pending") return "Bekliyor";
  return "Belirsiz";
}

function getQcStatusColor(row) {
  const status = String(row?.qc_status || "").toLowerCase();
  if (status === "approved") return "success.main";
  if (status === "rejected") return "error.main";
  if (status === "pending") return "warning.main";
  return "text.secondary";
}

export default function QualityControlApprovalsPage() {
  const [activeTab, setActiveTab] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pendingItems, setPendingItems] = useState([]);
  const [historyItems, setHistoryItems] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeRow, setActiveRow] = useState(null);
  const [qcNote, setQcNote] = useState("");
  const [qcImages, setQcImages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savingAction, setSavingAction] = useState("");
  const [saveError, setSaveError] = useState("");
  const fileInputRef = useRef(null);

  const items = activeTab === "history" ? historyItems : pendingItems;
  const pendingCount = useMemo(() => (Array.isArray(pendingItems) ? pendingItems.length : 0), [pendingItems]);

  const load = useCallback(async (targetTab = activeTab) => {
    setError("");
    setLoading(true);
    try {
      if (targetTab === "history") {
        const res = await getQualityApprovals("all");
        setHistoryItems(Array.isArray(res) ? res : []);
      } else {
        const res = await getPendingQualityApprovals();
        setPendingItems(Array.isArray(res) ? res : []);
      }
    } catch (err) {
      console.error("QC LOAD ERROR", err);
      const status = err?.response?.status;
      const data = err?.response?.data;
      const message =
        data?.error?.message ||
        data?.message ||
        (typeof data === "string" ? data : null) ||
        err?.message ||
        null;
      setError(`Kalite kontrol listesi alinamadi${status ? ` (HTTP ${status})` : ""}${message ? `: ${message}` : ""}`);
      if (targetTab === "history") setHistoryItems([]);
      else setPendingItems([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    load(activeTab);
  }, [activeTab, load]);

  function openApprove(row) {
    setActiveRow(row);
    setQcNote(row?.qc_note ? String(row.qc_note) : "");
    setQcImages([]);
    setSaveError("");
    setDialogOpen(true);
  }

  function closeApprove() {
    if (saving) return;
    setDialogOpen(false);
    setActiveRow(null);
    setQcNote("");
    setQcImages([]);
    setSavingAction("");
    setSaveError("");
  }

  async function onFilesSelected(event) {
    const files = Array.from(event?.target?.files || []);
    if (!files.length) return;
    try {
      const remaining = Math.max(0, 3 - qcImages.length);
      if (remaining <= 0) {
        setSaveError("En fazla 3 resim ekleyebilirsiniz.");
        return;
      }
      const parsed = await Promise.all(files.slice(0, remaining).map(readFileAsBase64));
      setQcImages((prev) => [...prev, ...parsed]);
      setSaveError("");
    } catch (err) {
      setSaveError(err?.message || "Resimler secilemedi");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  function removeQcImage(index) {
    setQcImages((prev) => prev.filter((_, idx) => idx !== index));
  }

  async function onDecision(decision) {
    if (!activeRow?.approval_id) return;
    setSaveError("");
    setSaving(true);
    setSavingAction(decision);
    try {
      const note = String(qcNote || "").trim();
      const existingImageCount = Array.isArray(activeRow?.images) ? activeRow.images.length : 0;
      if (existingImageCount + qcImages.length <= 0) {
        setSaveError(`Kalite ${decision === "reject" ? "reddi" : "onayi"} icin en az bir resim ekleyin.`);
        setSaving(false);
        setSavingAction("");
        return;
      }
      const payload = {
        qc_note: note ? note : null,
        qc_images: qcImages.map((img) => ({
          file_name: img.file_name,
          mime_type: img.mime_type,
          data_base64: img.data_base64,
        })),
      };
      if (decision === "reject") {
        await rejectQualityApproval(activeRow.approval_id, payload);
      } else {
        await approveQualityApproval(activeRow.approval_id, payload);
      }
      closeApprove();
      await Promise.all([load("pending"), load("history")]);
    } catch (err) {
      console.error("QC DECISION ERROR", err);
      const status = err?.response?.status;
      const data = err?.response?.data;
      const message =
        data?.error?.message ||
        data?.message ||
        (typeof data === "string" ? data : null) ||
        err?.message ||
        null;
      setSaveError(
        `QC ${decision === "reject" ? "reddi" : "onayi"} basarisiz${status ? ` (HTTP ${status})` : ""}${message ? `: ${message}` : ""}`
      );
    } finally {
      setSaving(false);
      setSavingAction("");
    }
  }

  return (
    <Stack
      spacing={2.5}
      sx={{
        maxWidth: 1540,
        mx: "auto",
        "& .MuiOutlinedInput-root": { borderRadius: 2.25, bgcolor: "#fff" },
      }}
    >
      <PageHeader
        title="Kalite Kontrol Onaylari"
        subtitle="Bekleyen kalite kayitlarina onay veya red verin, gecmisteki tum kalite kararlarini inceleyin."
        actions={
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              p: 1,
              borderRadius: 3,
              bgcolor: "#f5f3ff",
              border: "1px solid #c7d2fe",
              boxShadow: "0 8px 24px rgba(79, 70, 229, 0.08)",
            }}
          >
            <Typography variant="body2" sx={{ color: "#4338ca", fontWeight: 700, px: 0.75 }}>
              Bekleyen: <b>{pendingCount}</b>
            </Typography>
            <Button
              variant="contained"
              onClick={() => load(activeTab)}
              disabled={loading}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 800,
                bgcolor: "#4f46e5",
                boxShadow: "none",
                "&:hover": { bgcolor: "#3730a3", boxShadow: "none" },
              }}
            >
              Yenile
            </Button>
          </Stack>
        }
      />

      <Paper
        elevation={0}
        sx={{
          p: 0.75,
          borderRadius: 3.5,
          bgcolor: "#f5f3ff",
          border: "1px solid #c7d2fe",
          boxShadow: "0 10px 28px rgba(79, 70, 229, 0.08)",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          sx={{
            minHeight: 46,
            "& .MuiTabs-indicator": { display: "none" },
            "& .MuiTab-root": {
              minHeight: 44,
              borderRadius: 2.5,
              textTransform: "none",
              fontWeight: 800,
              color: "#64748b",
            },
            "& .Mui-selected": { bgcolor: "#4f46e5", color: "#fff !important", boxShadow: "0 6px 16px rgba(79, 70, 229, 0.24)" },
          }}
        >
          <Tab label="Bekleyen" value="pending" />
          <Tab label="Kalite Gecmisi" value="history" />
        </Tabs>
      </Paper>

      {loading ? <LinearProgress /> : null}
      {error ? <Alert severity="error">{error}</Alert> : null}

      {!loading && (!items || items.length === 0) ? (
        <EmptyState
          description={
            activeTab === "history"
              ? "Kayitli kalite gecmisi bulunamadi."
              : "Bekleyen kalite kontrol kaydi bulunamadi."
          }
        />
      ) : (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1, md: 1.5 },
            borderRadius: 4,
            bgcolor: "#f5f3ff",
            border: "1px solid #c7d2fe",
            boxShadow: "0 12px 30px rgba(79, 70, 229, 0.10)",
          }}
        >
          <TableContainer sx={{ maxHeight: "calc(100vh - 310px)", bgcolor: "#fff", borderRadius: 3, border: "1px solid #ddd6fe" }}>
            <Table
              stickyHeader
              size="small"
              sx={{
                "& .MuiTableCell-head": { bgcolor: "#ede9fe", color: "#3730a3", fontWeight: 800, borderColor: "#ddd6fe" },
                "& .MuiTableCell-root": { borderColor: "#e5e7eb" },
                "& .MuiTableBody-root .MuiTableRow-root:hover": { bgcolor: "#fafaff" },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>Is Emri</TableCell>
                  <TableCell>Operasyon</TableCell>
                  <TableCell>Is Merkezi</TableCell>
                  <TableCell align="right">Miktar</TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell>Cikis Lot</TableCell>
                  <TableCell>Onaylayan</TableCell>
                  <TableCell>Kalite</TableCell>
                  <TableCell align="right">Islem</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(items || []).map((row, idx) => {
                  const wcLabel = row?.work_center ? `${row.work_center.code || ""} ${row.work_center.name || ""}`.trim() : "-";
                  const canApprove = String(row?.qc_status || "").toLowerCase() === "pending";
                  return (
                    <TableRow
                      key={row.approval_id}
                      hover
                      sx={{ bgcolor: idx % 2 ? "rgba(15, 23, 42, 0.02)" : "transparent" }}
                    >
                      <TableCell>
                        <Typography sx={{ fontWeight: 800 }}>{row.order_no || "-"}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          ID: {row.production_order_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontWeight: 700 }}>OP {row.op_no}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          RoutingOp: {row.routing_operation_id}
                        </Typography>
                      </TableCell>
                      <TableCell>{wcLabel || "-"}</TableCell>
                      <TableCell align="right">{Number(row.approved_qty || 0)}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            color: getQcStatusColor(row),
                          }}
                        >
                          {getQcStatusLabel(row)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {canApprove ? fmtDateTime(row.approved_at) : fmtDateTime(row.qc_checked_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>{row.output_lot_no ? String(row.output_lot_no) : "-"}</TableCell>
                      <TableCell>
                        <Typography sx={{ fontWeight: 600 }}>{row.approved_by_name || "-"}</Typography>
                        {row.approved_by_employee_code ? (
                          <Typography variant="caption" color="text.secondary">
                            Çalışan kodu: {row.approved_by_employee_code}
                          </Typography>
                        ) : null}
                        <Typography variant="body2" color="text.secondary">
                          {fmtDateTime(row.approved_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{row.qc_note ? String(row.qc_note) : "-"}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Resim: {Array.isArray(row.images) ? row.images.length : 0}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {canApprove ? (
                          <Button size="small" variant="contained" onClick={() => openApprove(row)} disabled={saving}>
                            Incele
                          </Button>
                        ) : (
                          <Button size="small" variant="outlined" onClick={() => openApprove(row)} disabled={saving}>
                            Gor
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <FormDialog
        open={dialogOpen}
        maxWidth="md"
        paperSx={{ borderRadius: 4, overflow: "hidden", boxShadow: "0 24px 70px rgba(15, 23, 42, 0.22)" }}
        titleSx={{ px: { xs: 2.5, sm: 3.5 }, py: 2.5, color: "#fff", background: "linear-gradient(135deg, #312e81 0%, #4f46e5 56%, #6366f1 100%)" }}
        titleTextSx={{ color: "#fff" }}
        subtitleTextSx={{ color: "rgba(255,255,255,0.80)" }}
        contentSx={{ p: { xs: 2, sm: 3 }, bgcolor: "#f7f8fc", borderColor: "#e0e7ff" }}
        actionsSx={{ px: { xs: 2, sm: 3.5 }, py: 2, bgcolor: "#fff", borderTop: "1px solid #e0e7ff", "& .MuiButton": { borderRadius: 2, textTransform: "none", fontWeight: 800 }, "& .MuiButton-contained:not(.Mui-disabled)": { bgcolor: "#4f46e5", "&:hover": { bgcolor: "#3730a3" } } }}
        title={
          String(activeRow?.qc_status || "").toLowerCase() === "approved"
            ? "Kalite Kaydi"
            : String(activeRow?.qc_status || "").toLowerCase() === "rejected"
            ? "Kalite Red Kaydi"
            : "QC Karari"
        }
        subtitle={`${activeRow?.order_no || ""}${activeRow?.op_no ? ` | OP ${activeRow.op_no}` : ""}${
          activeRow?.output_lot_no ? ` | Lot: ${activeRow.output_lot_no}` : ""
        }`}
        onClose={closeApprove}
        onSubmit={() => onDecision("approve")}
        submitLabel="Onayla"
        loading={saving}
        actions={
          String(activeRow?.qc_status || "").toLowerCase() === "pending" ? (
            <>
              <Button onClick={closeApprove} color="inherit" disabled={saving}>
                Iptal
              </Button>
              <Button
                onClick={() => onDecision("reject")}
                color="error"
                variant="outlined"
                disabled={saving}
                startIcon={saving && savingAction === "reject" ? <CircularProgress size={16} /> : null}
              >
                {saving && savingAction === "reject" ? "Reddediliyor..." : "Reddet"}
              </Button>
              <Button
                onClick={() => onDecision("approve")}
                variant="contained"
                disabled={saving}
                startIcon={saving && savingAction === "approve" ? <CircularProgress size={16} /> : null}
              >
                {saving && savingAction === "approve" ? "Onaylaniyor..." : "Onayla"}
              </Button>
            </>
          ) : (
            <Button onClick={closeApprove} variant="contained" disabled={saving}>
              Kapat
            </Button>
          )
        }
      >
        <Stack spacing={2} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.25, bgcolor: "#fff" } }}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: "1px solid #c7d2fe", bgcolor: "#f5f3ff" }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="space-between">
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={800}>İŞ EMRİ</Typography>
                <Typography fontWeight={800}>{activeRow?.order_no || "-"}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={800}>OPERASYON</Typography>
                <Typography fontWeight={800}>OP {activeRow?.op_no || "-"}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={800}>ÇIKIŞ LOTU</Typography>
                <Typography fontWeight={800}>{activeRow?.output_lot_no || "-"}</Typography>
              </Box>
            </Stack>
          </Paper>
          {Array.isArray(activeRow?.images) && activeRow.images.length ? (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {activeRow.images.map((img) => {
                const imageUrl = buildImageUrl(img.file_path);
                return (
                  <Box
                    key={img.id}
                    component="a"
                    href={imageUrl || "#"}
                    target="_blank"
                    rel="noreferrer"
                    sx={{ display: "inline-flex" }}
                  >
                    <Box
                      component="img"
                      src={imageUrl || ""}
                      alt={img.file_name || "QC"}
                      sx={{
                        width: 88,
                        height: 88,
                        objectFit: "cover",
                        borderRadius: 1.5,
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    />
                  </Box>
                );
              })}
            </Stack>
          ) : null}

          <TextField
            label="QC Notu (opsiyonel)"
            value={qcNote}
            onChange={(e) => setQcNote(e.target.value)}
            placeholder="Kalite kontrol notu..."
            multiline
            minRows={3}
            disabled={saving || String(activeRow?.qc_status || "").toLowerCase() === "approved"}
          />

          {String(activeRow?.qc_status || "").toLowerCase() === "pending" ? (
            <>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button variant="outlined" onClick={() => fileInputRef.current?.click()} disabled={saving || qcImages.length >= 3}>
                  Resim Ekle
                </Button>
                <Typography variant="body2" color="text.secondary">
                  En fazla 3 resim
                </Typography>
              </Stack>

              <input ref={fileInputRef} type="file" accept="image/*" multiple hidden onChange={onFilesSelected} />

              {qcImages.length ? (
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {qcImages.map((img, idx) => (
                    <Stack key={`${img.file_name || "img"}-${idx}`} spacing={0.5} alignItems="flex-start">
                      <Box
                        component="img"
                        src={img.preview_url}
                        alt={img.file_name || `qc-${idx + 1}`}
                        sx={{
                          width: 88,
                          height: 88,
                          objectFit: "cover",
                          borderRadius: 1.5,
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      />
                      <Button size="small" color="error" onClick={() => removeQcImage(idx)}>
                        Kaldir
                      </Button>
                    </Stack>
                  ))}
                </Stack>
              ) : null}
            </>
          ) : null}

          {saveError ? <Alert severity="error">{saveError}</Alert> : null}
        </Stack>
      </FormDialog>
    </Stack>
  );
}
